import { AlertTriangle, Check } from "lucide-react"
import type { OrderDetail } from "../../services/use-orders"
import { CopyValue } from "../ui/copy-value"
import { StateChip } from "../../routes/orders.index"
import { cn } from "../../lib/utils"

export function AddressCorrectionPanel({
  correction,
}: {
  correction: NonNullable<OrderDetail["addressCorrection"]>
}) {
  if (correction.status === "manual_review") {
    return (
      <section className="space-y-3 p-4 sm:p-5" aria-label="Address needs a manual check">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <AlertTriangle className="size-4 text-warning" />
          Address needs a manual check
        </h3>

        <p className="text-sm">{correction.explanation}</p>

        {correction.rejectedFields.length > 0 ? (
          <p className="text-xs text-muted-foreground">
            Rejected fields: {correction.rejectedFields.join(", ")}
          </p>
        ) : null}

        <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>Reason code</span>

          <code className="rounded bg-muted px-1 py-0.5 text-foreground">{correction.code}</code>
        </p>
      </section>
    )
  }
  const { address, original } = correction
  const replaced = new Set(correction.replaced)
  const province = `${address.province.name}${address.province.code ? ` (${address.province.code})` : ""}`
  const completeAddress = [
    address.streetLine,
    address.city,
    address.postcode,
    province,
    address.country,
  ].join("\n")
  const rows = [
    {
      label: "Street",
      value: address.streetLine,
      old: original?.streetLines.join("\n"),
      changed: replaced.has("street"),
    },
    { label: "City", value: address.city, old: original?.city, changed: replaced.has("city") },
    {
      label: "Postcode",
      value: address.postcode,
      old: original?.postcode,
      changed: replaced.has("postcode"),
    },
    { label: "Province / state", value: province, old: original?.region, suggested: true },
    { label: "Country", value: address.country, old: original?.country },
    ...(address.deliveryInstruction
      ? [{ label: "Delivery note", value: address.deliveryInstruction, old: null }]
      : []),
  ]
  return (
    <section className="p-4 sm:p-5" aria-label="Suggested fix: corrected address">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Suggested fix: corrected address</h3>

        <StateChip kind="good">
          <Check className="mr-1 size-3.5" />
          Carrier accepts it
        </StateChip>
      </div>

      <p className="mt-2 text-sm text-muted-foreground">
        Nova changed {replaced.size} fields. Copy it into OMS, then resolve below.
      </p>

      <div
        className={cn(
          "mt-4 hidden gap-3 border-b border-border pb-2 text-xs text-muted-foreground sm:grid",
          original
            ? "sm:grid-cols-[110px_minmax(0,1fr)_minmax(0,1fr)]"
            : "sm:grid-cols-[110px_minmax(0,1fr)]"
        )}
        aria-hidden="true"
      >
        <span>Field</span>

        {original ? <span>On the order</span> : null}

        <span>Corrected</span>
      </div>

      <dl className="divide-y divide-border/70">
        {rows.map((row) => (
          <div
            key={row.label}
            className={cn(
              "grid min-w-0 gap-2 py-3 text-sm sm:gap-3",
              original
                ? "sm:grid-cols-[110px_minmax(0,1fr)_minmax(0,1fr)]"
                : "sm:grid-cols-[110px_minmax(0,1fr)]"
            )}
          >
            <dt className="text-xs font-medium text-muted-foreground">
              {row.label}

              {row.suggested ? (
                <span className="mt-1 block text-xs text-info">AI suggested</span>
              ) : null}
            </dt>

            {original ? (
              <dd className="min-w-0 whitespace-pre-line text-muted-foreground">
                <span className="mr-2 text-xs sm:sr-only">On the order: </span>

                <span className={row.changed ? "line-through" : undefined}>
                  {row.old ?? "Not available"}
                </span>
              </dd>
            ) : null}

            <dd className="min-w-0 whitespace-pre-line">
              <span className="mr-2 text-xs text-muted-foreground sm:sr-only">Corrected: </span>

              <span className={row.changed ? "font-semibold" : undefined}>{row.value}</span>

              {row.changed ? (
                <span className="ml-2 inline-flex">
                  <StateChip kind="info">Changed</StateChip>

                  <span className="sr-only"> (replaced)</span>
                </span>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>

      <div className="mt-2 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:flex-wrap sm:items-center">
        <p className="text-xs text-muted-foreground sm:mr-auto">Country comes from live Magento.</p>

        <CopyValue
          value={completeAddress}
          label="Copy full address"
          className="min-h-11 w-full justify-center rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:text-primary-foreground focus-visible:outline-2 focus-visible:outline-ring sm:order-3 sm:w-auto"
        />

        <CopyValue
          value={address.streetLine}
          label="Copy street"
          className="min-h-11 w-full justify-center rounded-full border border-border px-4 py-2 text-sm font-medium sm:order-2 sm:w-auto"
        />
      </div>
    </section>
  )
}
